// apps/web/src/state/tourStore.ts
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useBrainStore } from '@/state/brainStore';

export interface TourBaseline {
  outbound: number;
  depositMsgs: number;
}

export type TourOutcome = 'complete' | 'handoff';

interface TourState {
  enabled: boolean;
  activeTourId: string | null;
  activeCustomerId: string | null;
  activeStepId: string | null;
  baseline: TourBaseline | null;
  completed: Record<string, boolean>;
  lastOutcome: TourOutcome | null;
  setEnabled: (on: boolean) => void;
  start: (tourId: string, customerId: string) => void;
  setActiveStep: (stepId: string | null) => void;
  complete: (tourId: string, outcome: TourOutcome) => void;
  clearOutcome: () => void;
  stop: () => void;
}

function baselineFor(customerId: string): TourBaseline {
  const conv = useBrainStore.getState().customers[customerId]?.conversation ?? [];
  return {
    outbound: conv.filter((e) => e.direction === 'out').length,
    depositMsgs: conv.filter((e) => Boolean(e.depositId)).length,
  };
}

export const useTourStore = create<TourState>()(
  persist(
    (set, get) => ({
      enabled: true,
      activeTourId: null,
      activeCustomerId: null,
      activeStepId: null,
      baseline: null,
      completed: {},
      lastOutcome: null,
      // Off PAUSES: the overlay hides (TourGuide gates on `enabled`) and the
      // controller stops syncing, but the active tour/step is preserved so
      // re-enabling resumes at the user's current position. Turning it ON also
      // clears `completed` so a finished tour replays — otherwise the toggle
      // looks dead once you've been through the walkthrough (the persisted
      // `completed[tourId]` flag makes start() a no-op, which read as "the toggle
      // doesn't do anything"). A paused active tour isn't in `completed`, so
      // resume-in-place is unaffected. Use stop() to actually end a tour.
      setEnabled: (on) => set(() => (on ? { enabled: true, completed: {} } : { enabled: false })),
      start: (tourId, customerId) => {
        const s = get();
        if (!s.enabled || s.completed[tourId]) return;
        if (s.activeTourId === tourId && s.activeCustomerId === customerId) return;
        set({
          activeTourId: tourId,
          activeCustomerId: customerId,
          baseline: baselineFor(customerId),
          activeStepId: null,
          lastOutcome: null,
        });
      },
      setActiveStep: (stepId) => set({ activeStepId: stepId }),
      complete: (tourId, outcome) =>
        set((s) => ({
          completed: { ...s.completed, [tourId]: true },
          lastOutcome: outcome,
          activeTourId: null,
          activeStepId: null,
        })),
      clearOutcome: () => set({ lastOutcome: null }),
      stop: () => set({ activeTourId: null, activeStepId: null }),
    }),
    {
      name: 'ati-tour',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ enabled: s.enabled, completed: s.completed }),
    },
  ),
);
